import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, AlertTriangle } from "lucide-react";
import type {
	PageIssue,
	RedirectDetails,
	RedirectFix,
} from "@/types/technicalAudit";
import { cn } from "@/lib/utils";

interface RedirectChainCardProps {
	issue: PageIssue;
}

export function RedirectChainCard({ issue }: RedirectChainCardProps) {
	const details = (issue.issue_details ?? {}) as RedirectDetails;
	const proposedFix = issue.proposed_fix as RedirectFix | null;
	const chain = Array.isArray(details.redirect_chain)
		? details.redirect_chain
		: [];
	const redirectCount =
		typeof details.redirect_count === "number"
			? details.redirect_count
			: chain.length;

	// A flagged redirect can arrive without a captured chain (e.g. a
	// recommendation-only "point links at the final URL"). Render the available
	// info instead of crashing on .map() over an undefined chain.
	if (chain.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Redirect</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm">
					{details.final_url ? (
						<p className="break-all font-mono">
							Final URL: {details.final_url}
						</p>
					) : null}
					<p className="text-muted-foreground">
						No redirect-chain detail was captured for this issue. Recommended
						action: point internal links directly at the final destination URL.
					</p>
				</CardContent>
			</Card>
		);
	}

	const isExcessiveChain = redirectCount > 3;

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base flex items-center gap-2">
					Redirect Chain
					{isExcessiveChain && (
						<Badge variant="destructive" className="text-xs">
							Excessive redirects
						</Badge>
					)}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-6">
				{/* Current Chain */}
				<div>
					<div className="mb-3 text-sm font-medium text-muted-foreground">
						Current Chain ({details.redirect_count}{" "}
						{details.redirect_count === 1 ? "hop" : "hops"})
					</div>

					<div className="space-y-3">
						{details.redirect_chain.map((url, index) => {
							const isLast = index === details.redirect_chain.length - 1;
							const statusCode =
								index === details.redirect_chain.length - 1 ? 200 : 301;

							return (
								// biome-ignore lint/suspicious/noArrayIndexKey: Redirect chain order is stable and meaningful
								<div key={index} className="space-y-2">
									<div className="flex items-start gap-3">
										<Badge
											variant={statusCode === 200 ? "success" : "warning"}
											className="mt-1 flex-shrink-0"
										>
											{statusCode}
										</Badge>
										<div className="flex-1 min-w-0">
											<p className="text-sm break-all font-mono">{url}</p>
											{index === 0 && (
												<p className="text-xs text-muted-foreground mt-1">
													Original URL
												</p>
											)}
											{isLast && (
												<p className="text-xs text-green-600 dark:text-green-400 mt-1">
													Final destination
												</p>
											)}
										</div>
									</div>

									{!isLast && (
										<div className="flex items-center gap-2 pl-20">
											<ArrowRight
												className={cn(
													"h-4 w-4",
													isExcessiveChain && "text-red-500 dark:text-red-400",
												)}
											/>
											<span className="text-xs text-muted-foreground">
												Redirects to
											</span>
										</div>
									)}
								</div>
							);
						})}
					</div>

					{isExcessiveChain && (
						<div className="mt-4 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/40 p-3">
							<AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
							<div>
								<p className="text-sm font-medium text-red-800 dark:text-red-300">
									Too many redirects
								</p>
								<p className="text-xs text-red-700 dark:text-red-400 mt-1">
									Redirect chains slow down page load times and can impact SEO.
									Keep redirects to a maximum of 1-2 hops.
								</p>
							</div>
						</div>
					)}
				</div>

				{/* Recommended Fix */}
				{proposedFix?.recommended_target && (
					<div>
						<div className="mb-3 text-sm font-medium text-green-600 dark:text-green-400">
							Recommended Fix
						</div>

						<div className="space-y-3">
							<div className="flex items-start gap-3">
								<Badge variant="warning" className="mt-1 flex-shrink-0">
									301
								</Badge>
								<div className="flex-1 min-w-0">
									<p className="text-sm break-all font-mono">
										{details.redirect_chain[0]}
									</p>
									<p className="text-xs text-muted-foreground mt-1">
										Original URL
									</p>
								</div>
							</div>

							<div className="flex items-center gap-2 pl-20">
								<ArrowRight className="h-5 w-5 text-green-600 dark:text-green-400" />
								<span className="text-xs font-medium text-green-600 dark:text-green-400">
									Direct redirect to
								</span>
							</div>

							<div className="flex items-start gap-3">
								<Badge variant="success" className="mt-1 flex-shrink-0">
									200
								</Badge>
								<div className="flex-1 min-w-0">
									<p className="text-sm break-all font-mono">
										{proposedFix.recommended_target}
									</p>
									<p className="text-xs text-green-600 dark:text-green-400 mt-1">
										Final destination
									</p>
								</div>
							</div>
						</div>

						{proposedFix.reasoning && (
							<div className="mt-4 rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
								<p className="text-xs font-medium text-blue-800 dark:text-blue-300">
									AI Reasoning:
								</p>
								<p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
									{proposedFix.reasoning}
								</p>
							</div>
						)}

						<div className="mt-4 rounded-md bg-green-50 dark:bg-green-950/40 p-3">
							<p className="text-xs font-medium text-green-800 dark:text-green-300">Impact:</p>
							<p className="text-xs text-green-700 dark:text-green-400 mt-1">
								This change will reduce the redirect chain from{" "}
								{details.redirect_count}{" "}
								{details.redirect_count === 1 ? "hop" : "hops"} to 1 hop,
								improving page load time and SEO performance.
							</p>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
